// Slowly morphing contour rings - the same motif as app.thesoma.ai. Drawn behind the
// research band on the home page, behind the contact page and on the docs landing.

const RINGS = 22;
const SEGMENTS = 96;
// 1-based index of the ring drawn in the accent color.
const ACCENT_RING = 7;

export interface ContourColors {
  line: string;
  accent: string;
}

/** Where the rings sit: which tokens paint them and where their centre is. */
export interface ContourStyle {
  lineToken: string;
  accentToken: string;
  /** Centre as a fraction of the canvas width and height. */
  origin: { x: number; y: number };
}

// "band" is the always-dark research band; "page" sits on the ordinary page background
// and follows the theme, so it reads the semantic tokens rather than the band ones.
export const CONTOUR_STYLES = {
  band: { lineToken: "--color-band-line-2", accentToken: "--color-band-accent", origin: { x: 0.55, y: 0.42 } },
  page: { lineToken: "--color-line-strong", accentToken: "--color-accent", origin: { x: 0.5, y: 0.38 } },
} satisfies Record<string, ContourStyle>;

export type ContourVariant = keyof typeof CONTOUR_STYLES;

export function drawContours(
  ctx: CanvasRenderingContext2D,
  widthPx: number,
  heightPx: number,
  tS: number,
  colors: ContourColors,
  origin: ContourStyle["origin"] = CONTOUR_STYLES.band.origin,
): void {
  ctx.clearRect(0, 0, widthPx, heightPx);
  const cx = widthPx * origin.x;
  const cy = heightPx * origin.y;
  const maxR = Math.max(widthPx, heightPx) * 0.62;

  for (let i = 1; i <= RINGS; i++) {
    const r = (maxR * i) / RINGS;
    const isAccent = i === ACCENT_RING;
    ctx.beginPath();
    for (let a = 0; a <= SEGMENTS; a++) {
      const th = (a / SEGMENTS) * Math.PI * 2;
      const warp =
        1 +
        0.06 * Math.sin(th * 3 + tS * 0.25 + i * 0.35) +
        0.04 * Math.sin(th * 5 - tS * 0.18 + i * 0.2) +
        0.03 * Math.cos(th * 2 + tS * 0.1);
      const x = cx + Math.cos(th) * r * warp * 1.15;
      const y = cy + Math.sin(th) * r * warp * 0.82;
      if (a === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = isAccent ? colors.accent : colors.line;
    ctx.globalAlpha = isAccent ? 0.9 : Math.max(0.25, 1 - (i / RINGS) * 0.8);
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}
